/* eslint-disable no-shadow */
import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, {
  B2ChatClientBroadcast,
  apiResource,
  b2chatBaseQuery,
} from '@src/client';

export enum ServiceMessageReportStatus {
  InProgress = 'IN_PROGRESS',
  Closed = 'CLOSED',
}

export enum ServiceMessageCountryGroup {
  LatamRest = 'LATAM_REST',
  Unknown = 'UNKNOWN',
  Other = 'OTHER',
}

export interface ServiceMessageDailyItem {
  date: string; // 'yyyy-MM-dd'
  messages: number;
  billable: number;
  notBilled: number;
}

export interface ServiceMessageNotBilled {
  messages: number;
  percent: number;
  clickToWhatsApp: number;
  monthlyFreeQuota: number;
  other: number;
  /**
   * Messages of the AI agent that Meta billed but the merchant does not pay:
   * to Colombia under the AI-conversation rate, and from agents in testing to
   * any country. Absent until the AI-conversation billing epic ships; then
   * always present (0 before its first month).
   */
  aiAgent?: number;
}

export interface ServiceMessageTotals {
  messages: number;
  billable: number;
  billablePercent: number;
  notBilled: ServiceMessageNotBilled;
}

export interface ServiceMessageLine {
  identifier: string;
  alias: string;
}

export interface ServiceMessageReport {
  month: string; // 'yyyy-MM'
  status: ServiceMessageReportStatus;
  lastDataAt: string | null;
  totals: ServiceMessageTotals;
  daily: ServiceMessageDailyItem[];
  filters: {
    lines: ServiceMessageLine[];
    countries: string[];
  };
}

export interface ServiceMessageReportParams {
  month: string; // 'yyyy-MM'
  line?: string;
  country?: string;
}

export interface CreateServiceMessageReportRequest {
  emails: string[];
  nameFile: string;
  locate: string;
  filters: {
    merchantId: string;
    month: string;
    line?: string;
    country?: string;
  };
}

export interface CreateServiceMessageReportResponse {
  code: number;
  message: string;
}

const serviceMessagesApi = createApi({
  reducerPath: 'serviceMessagesApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['serviceMessageReport'],
  endpoints: ({ query, mutation }) => ({
    getServiceMessageReport: query<
      ServiceMessageReport,
      ServiceMessageReportParams
    >({
      providesTags: (_r, _e, { month }) => [
        { type: 'serviceMessageReport', id: month },
      ],
      query: ({ month, line, country }) =>
        apiResource(
          B2ChatClient.resources.serviceMessageReport.actions
            .getServiceMessageReport,
          {
            params: {
              month,
              ...(line ? { line } : {}),
              ...(country ? { country } : {}),
            },
          }
        ),
    }),
    createServiceMessageReport: mutation<
      CreateServiceMessageReportResponse,
      CreateServiceMessageReportRequest
    >({
      query: data =>
        apiResource(
          B2ChatClientBroadcast.resources.createServiceMessageReport.actions
            .report,
          {
            params: { merchantId: data.filters.merchantId },
            data,
          }
        ),
    }),
  }),
});

export const {
  useGetServiceMessageReportQuery,
  useLazyGetServiceMessageReportQuery,
  useCreateServiceMessageReportMutation,
} = serviceMessagesApi;

export default serviceMessagesApi;
